import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { Button } from "../Button";

interface NumberInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string | React.ReactNode;
  value?: string;
  error?: string | null;
  placeholder?: string;
  className?: string;
  faIcon?: IconProp;
  iconSrc?: string;
  iconText?: string;
  isRequired?: boolean | false;
  variant?: "default" | "auth";
  onButtonClick?: () => void;
  disabled?: boolean;
  inputClassName?: string;
  faIconClassName?: string;
}

export function NumberInput({
  label,
  value,
  error,
  placeholder,
  faIcon,
  iconSrc,
  iconText,
  className,
  variant = "default",
  isRequired,
  onButtonClick,
  disabled,
  inputClassName,
  faIconClassName,
  ...props
}: NumberInputProps) {

  const handleLengthLimit = (event: React.FormEvent<HTMLInputElement>) => {
    const maxLength = props.maxLength;
    if (!maxLength || maxLength < 0) return;

    const target = event.currentTarget;
    if (target.value.length > maxLength) {
      target.value = target.value.slice(0, maxLength);
    }
  };

  return (
    <div className={`mb-2 ${className}`}>
      {label && (
        typeof label === "string" ? (
          <label className="block text-[#A9A9A9] mb-1 ml-1 font-medium">
            {label} {isRequired && <span className="text-red-500">*</span>}
          </label>
        ) : (
          label
        )
      )}
      <div className="relative">
        <input
          {...props}
          type="number"
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            handleLengthLimit(e);
            props.onChange?.(e);
          }}
          onInput={(e) => {
            handleLengthLimit(e);
            props.onInput?.(e);
          }}
          disabled={disabled}
          className={`
                  w-full rounded-lg transition text-white text-sm font-medium
                  focus:outline-none focus:ring-2 focus:ring-[#333333]
                  ${variant === "default" ? "bg-[#000000]" : "bg-[#1D1D1D]"} border-2
                  ${error ? "border-red-500" : "border-[#333333]"}
                  ${inputClassName || "px-4 py-2 h-12"}
                  appearance-none
                  [&::-webkit-inner-spin-button]:appearance-none
                  [&::-webkit-outer-spin-button]:appearance-none
                  [&::-webkit-inner-spin-button]:m-0
                  [&::-webkit-outer-spin-button]:m-0
                `}
          onKeyDown={(e) => {
            if (
              !/[0-9]/.test(e.key) &&
              e.key !== "Backspace" &&
              e.key !== "Delete" &&
              e.key !== "ArrowLeft" &&
              e.key !== "ArrowRight" &&
              e.key !== "Tab"
            ) {
              e.preventDefault();
            }
          }}
        />

        {faIcon && (
          <Button
            type="button"
            className={`absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#C71B4A99] flex items-center gap-1 text-sm `}
            onClick={onButtonClick}
            disabled={onButtonClick ? false : true}
          >
            <div
              className={`${faIconClassName || "bg-black text-white p-2 hover:bg-[#C71B4A99]"}`}
            >
              <FontAwesomeIcon icon={faIcon} />
              {iconText && <span>{iconText}</span>}
            </div>
          </Button>
        )}
        {iconSrc && (
          <Button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#C71B4A99] flex items-center gap-1 text-sm"
            onClick={onButtonClick}
            disabled={onButtonClick ? false : true}
          >
            <img src={iconSrc} alt="icon" className="w-6 h-6" />
            {iconText && <span>{iconText}</span>}
          </Button>
        )}
      </div>
      {error && <div className="text-red-500 text-s mt-1 ml-2">{error}</div>}
    </div>
  );
}
